/**
 * Auth Store - Zustand store for authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import { api } from '../api/client';
import type { User, ProfileUpdate } from '../api/types';
import { useThemeStore } from './themeStore';

function applyUserTheme(user: User) {
  if (user.theme_preference) {
    useThemeStore.getState().applyServerTheme(user.theme_preference);
  }
}

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Admin preview mode: when an ADMIN user clicks "View as Accountant",
  // this flips to 'accountant' and the AccountantLayout gate lets them
  // through. Persisted so a refresh keeps them in preview.
  previewSegment: 'accountant' | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (credential: string) => Promise<{ isNewUser: boolean }>;
  logout: () => void;
  register: (email: string, password: string, accepted_terms?: boolean, cookie_consent?: boolean) => Promise<void>;
  updateProfile: (data: ProfileUpdate) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setPreviewSegment: (seg: 'accountant' | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      isAuthenticated: !!api.token.get(),
      isLoading: true,
      error: null,
      previewSegment: null,

      // Login action
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          await api.auth.login(email, password);
          const user = await api.auth.getMe();
          applyUserTheme(user);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          const status = isAxiosError(error) ? error.response?.status : undefined;
          let message: string;
          if (status === 403) {
            message = 'EMAIL_NOT_VERIFIED';
          } else if (status === 401) {
            message = 'Invalid email or password. Please try again.';
          } else {
            message = error instanceof Error ? error.message : 'Login failed';
          }
          set({ error: message, isLoading: false, isAuthenticated: false });
          throw error;
        }
      },

      // Google OAuth login. Returns { isNewUser } so the signup wizard can
      // distinguish a fresh sign-up (→ Step 2) from a returning user
      // (→ /dashboard) without relying on signup_segment, which is null for
      // any user that abandoned at step 2.
      googleLogin: async (credential: string) => {
        set({ isLoading: true, error: null });
        try {
          const resp = await api.auth.googleLogin(credential);
          const user = await api.auth.getMe();
          applyUserTheme(user);
          set({ user, isAuthenticated: true, isLoading: false });
          return { isNewUser: Boolean(resp.is_new_user) };
        } catch (error) {
          const message =
            (isAxiosError(error) && error.response?.data?.detail) ||
            (error instanceof Error ? error.message : 'Google sign-in failed');
          set({ error: message, isLoading: false, isAuthenticated: false });
          throw error;
        }
      },

      // Logout action
      logout: () => {
        api.token.clear();
        // Scrub any in-flight signup wizard state so a shared browser
        // doesn't leak Company A's profile into User B's next signup.
        try {
          sessionStorage.removeItem('credia_signup_state_v2');
          sessionStorage.removeItem('credia_signup_state_v1');
          sessionStorage.removeItem('currentDealId');
          sessionStorage.removeItem('currentTermSheetId');
        } catch {
          // sessionStorage can throw in incognito/SSR — non-fatal.
        }
        set({ user: null, isAuthenticated: false, error: null, previewSegment: null });
        window.location.href = '/login';
      },

      // Register action - creates account, user must verify email before login
      register: async (email: string, password: string, accepted_terms: boolean = true, cookie_consent: boolean = false) => {
        set({ isLoading: true, error: null });
        try {
          await api.auth.register(email, password, accepted_terms, cookie_consent);
          set({ isLoading: false });
        } catch (error) {
          const status = isAxiosError(error) ? error.response?.status : undefined;
          const message =
            status === 400
              ? 'An account with this email already exists.'
              : error instanceof Error
              ? error.message
              : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Update user profile — deliberately does NOT set isLoading so the
      // global ProtectedRoute spinner doesn't flash during inline PATCH calls
      // (e.g. signup wizard step transitions).
      updateProfile: async (data: ProfileUpdate) => {
        set({ error: null });
        try {
          const updatedUser = await api.auth.updateProfile(data);
          set({ user: updatedUser });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Profile update failed';
          set({ error: message });
          throw error;
        }
      },

      // Change password
      changePassword: async (currentPassword: string, newPassword: string) => {
        await api.auth.changePassword(currentPassword, newPassword);
      },

      // Delete account (soft delete)
      deleteAccount: async (password: string) => {
        await api.auth.deleteAccount(password);
        api.token.clear();
        set({ user: null, isAuthenticated: false, error: null, previewSegment: null });
        window.location.href = '/login';
      },

      // Check authentication status
      checkAuth: async () => {
        const token = api.token.get();
        if (!token) {
          set({ isAuthenticated: false, isLoading: false, user: null });
          return;
        }

        // If token is already past its expiry, skip refresh (would 401) and log out silently
        if (api.token.isExpired()) {
          api.token.clear();
          set({ isAuthenticated: false, isLoading: false, user: null });
          return;
        }

        // If token is expiring soon (within 5 min), proactively refresh while it's still valid
        if (api.token.isExpiringSoon()) {
          try {
            await api.auth.refresh();
          } catch {
            set({ isAuthenticated: false, isLoading: false, user: null });
            return;
          }
        }

        try {
          const user = await api.auth.getMe();
          applyUserTheme(user);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          api.token.clear();
          set({ isAuthenticated: false, isLoading: false, user: null });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Admin preview mode setter (used by AdminViewToggle + AccountantLayout
      // exit-preview link). Persisted via partialize so a refresh keeps the
      // admin in preview.
      setPreviewSegment: (seg) => set({ previewSegment: seg }),
    }),
    {
      name: 'credia-auth',
      partialize: (state) => ({
        // Only persist user data, not loading/error states
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        previewSegment: state.previewSegment,
      }),
    }
  )
);

// Initialize auth check on app load
if (typeof window !== 'undefined') {
  useAuthStore.getState().checkAuth();
}

/**
 * Derived hook: does the current user have PRO-level access?
 *
 * Returns true for:
 *  - subscription_tier = PRO | ENTERPRISE
 *  - user_role = ADMIN | OPS  (internal staff bypass)
 *
 * This is the single source of truth for feature gating.
 * It does NOT depend on the Stripe Subscription DB record,
 * so manually upgraded users and OPS accounts work correctly.
 */
export function useIsPro(): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) {return false;}

  const tier = user.subscription_tier?.toUpperCase();
  const role = user.user_role?.toUpperCase();

  if (role === 'ADMIN' || role === 'OPS') {return true;}
  if (tier === 'PRO' || tier === 'ENTERPRISE') {return true;}

  return false;
}
